import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { MessageSquare, Send, Loader as Loader2, CircleCheck as CheckCircle2, Circle as XCircle, TriangleAlert as AlertTriangle, Smartphone, Building2 } from 'lucide-react';

interface SelectedClient {
  installment_id: string;
  client_name: string;
  phone: string;
  amount: number;
  due_date: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selected: SelectedClient[];
  onSent?: (result: any) => void;
}

interface TemplateOption {
  value: string;
  label: string;
  description: string;
}

const TEMPLATES: TemplateOption[] = [
  { value: 'before_due', label: 'Lembrete antes do vencimento', description: 'Aviso amigável antes de vencer' },
  { value: 'on_due_day', label: 'Lembrete no dia do vencimento', description: 'Aviso do dia do pagamento' },
  { value: 'overdue_1d', label: 'Atraso de 1 dia', description: 'Primeira cobrança pós-vencimento' },
  { value: 'overdue_3d', label: 'Atraso de 3 dias', description: 'Segunda cobrança' },
  { value: 'overdue_7d', label: 'Atraso de 7 dias', description: 'Cobrança firme' },
  { value: 'payment_confirmation', label: 'Confirmação de pagamento', description: 'Agradecimento pelo pagamento' },
];

async function callFn(path: string, body: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
  return data;
}

export default function WhatsAppSendModal({ isOpen, onClose, selected, onSent }: Props) {
  const [template, setTemplate] = useState<string>('before_due');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [senderInfo, setSenderInfo] = useState<{ scope: 'seller' | 'unit' | 'none'; connected: boolean } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setResult(null);
      setError(null);
      checkSender();
    }
  }, [isOpen]);

  const checkSender = async () => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) return;
    const { data: profile } = await supabase
      .from('users')
      .select('unit_id')
      .eq('id', userData.user.id)
      .maybeSingle();
    if (!profile?.unit_id) return;

    const { data: seller } = await supabase
      .from('whatsapp_instances')
      .select('status')
      .eq('scope', 'seller')
      .eq('seller_id', userData.user.id)
      .maybeSingle();
    if (seller?.status === 'connected') {
      setSenderInfo({ scope: 'seller', connected: true });
      return;
    }
    const { data: unit } = await supabase
      .from('whatsapp_instances')
      .select('status')
      .eq('scope', 'unit')
      .eq('unit_id', profile.unit_id)
      .maybeSingle();
    if (unit?.status === 'connected') {
      setSenderInfo({ scope: 'unit', connected: true });
    } else {
      setSenderInfo({ scope: 'none', connected: false });
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const res = await callFn('whatsapp-send-batch', {
        template_type: template,
        installment_ids: selected.map((s) => s.installment_id),
      });
      setResult(res);
      onSent?.(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const validPhones = selected.filter((s) => (s.phone || '').replace(/\D/g, '').length >= 10);
  const invalidCount = selected.length - validPhones.length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <MessageSquare className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Enviar WhatsApp em lote</h3>
              <p className="text-sm text-slate-500">{selected.length} cliente(s) selecionado(s)</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {senderInfo && (
            <div className={`p-3 rounded-lg border flex items-center gap-3 ${
              senderInfo.scope === 'seller'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : senderInfo.scope === 'unit'
                ? 'bg-blue-50 border-blue-200 text-blue-900'
                : 'bg-red-50 border-red-200 text-red-900'
            }`}>
              {senderInfo.scope === 'seller' ? (
                <Smartphone className="w-5 h-5" />
              ) : senderInfo.scope === 'unit' ? (
                <Building2 className="w-5 h-5" />
              ) : (
                <AlertTriangle className="w-5 h-5" />
              )}
              <div className="text-sm">
                {senderInfo.scope === 'seller' && (
                  <p><strong>Seu WhatsApp</strong> será usado no envio.</p>
                )}
                {senderInfo.scope === 'unit' && (
                  <p><strong>WhatsApp da unidade</strong> será usado (você não tem WhatsApp pessoal conectado).</p>
                )}
                {senderInfo.scope === 'none' && (
                  <p>Nenhum WhatsApp conectado. Sincronize em "Meu WhatsApp" ou peça ao gerente para conectar o da unidade.</p>
                )}
              </div>
            </div>
          )}

          {!result && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Template da mensagem</label>
                <div className="space-y-2">
                  {TEMPLATES.map((t) => (
                    <label
                      key={t.value}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        template === t.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="template"
                        value={t.value}
                        checked={template === t.value}
                        onChange={(e) => setTemplate(e.target.value)}
                        className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm">{t.label}</p>
                        <p className="text-xs text-slate-500">{t.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Destinatários ({validPhones.length} válidos{invalidCount > 0 ? `, ${invalidCount} sem telefone` : ''})
                </p>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {selected.map((s) => {
                    const valid = (s.phone || '').replace(/\D/g, '').length >= 10;
                    return (
                      <div key={s.installment_id} className="p-2.5 flex items-center gap-2 text-sm">
                        <div className={`w-2 h-2 rounded-full ${valid ? 'bg-emerald-500' : 'bg-red-400'}`} />
                        <span className="flex-1 truncate text-slate-800">{s.client_name}</span>
                        <span className="text-slate-500 text-xs">{s.phone || 'sem telefone'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  <p className="font-medium text-emerald-900">Envio concluído</p>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs text-slate-500">Selecionados</p>
                    <p className="font-bold text-slate-900">{result.total_selected}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs text-slate-500">Enviados</p>
                    <p className="font-bold text-emerald-700">{result.total_sent}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs text-slate-500">Ignorados</p>
                    <p className="font-bold text-amber-700">{result.total_skipped}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-xs text-slate-500">Falhas</p>
                    <p className="font-bold text-red-700">{result.total_failed}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 text-center">Confira o histórico completo em WhatsApp {'>'}Logs.</p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg font-medium transition-colors"
          >
            {result ? 'Fechar' : 'Cancelar'}
          </button>
          {!result && (
            <button
              onClick={handleSend}
              disabled={sending || validPhones.length === 0 || senderInfo?.scope === 'none'}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Enviando...' : `Enviar para ${validPhones.length}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
